// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import {
	type ComponentType,
	type FC,
	Fragment,
	type PropsWithChildren,
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import { FormattedMessage, defineMessages } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';

import Image from '@atlaskit/image';

import { Text } from '@atlaskit/primitives/compiled/text';

import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import type {
	IssuePreviewPanelSpotlightInlineRenderProps,
	IssuePreviewPanelSpotlightSidebarRenderProps,
} from '@post-office/browser--jira-empanada/issue-navigator-placement';

import issuePreviewPanelSpotlightImage from '../assets/issue-preview-panel-spotlight.png';

import { SpotlightPrimaryLink } from '@atlaskit/spotlight/primary-link';

import { SpotlightMedia } from '@atlaskit/spotlight/media';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent, type PopoverContentProps } from '@atlaskit/spotlight/popover-content';

const COMMUNITY_POST_URL =
	'https://community.atlassian.com/forums/Jira-articles/Preview-Panels-will-soon-replace-the-detail-view-in-Jira-s/ba-p/3196999';

const messages = defineMessages({
	body: {
		id: 'jira-nin-issue-preview-panel.body',
		defaultMessage: 'View and edit work items in the new preview panel, while staying on the List.',
		description: 'Body for the issue preview panel spotlight',
	},
	dismiss: {
		id: 'jira-nin-issue-preview-panel.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Dismiss button for the issue preview panel spotlight',
	},
	heading: {
		id: 'jira-nin-issue-preview-panel.heading',
		defaultMessage: 'Access work items instantly',
		description: 'Heading for the issue preview panel spotlight',
	},
	more: {
		id: 'jira-nin-issue-preview-panel.more',
		defaultMessage: 'More about preview panels',
		description: 'Learn more link for the issue preview panel spotlight',
	},
	newWindow: {
		id: 'jira-nin-issue-preview-panel.new-window',
		defaultMessage: '(opens in a new window)',
		description: 'New window text for the issue preview panel spotlight’s learn more link',
	},
});

const RenderToOpenIssueLink: FC<PropsWithChildren> = ({ children }) => {
	const renderInline = useMountToHtmlElement({
		selectors:
			'[data-testid="native-issue-table.common.ui.issue-cells.issue-summary.open-issue-link"]',
		as: 'inline',
		position: 'inside',
	});

	return renderInline(children);
};

type IssuePreviewPanelSpotlightUiProps = Pick<PopoverContentProps, 'offset' | 'placement'> & {
	TargetWrapper?: ComponentType<PropsWithChildren>;
	setIsActive: (isActive: boolean) => void;
};

const IssuePreviewPanelSpotlightUi: FC<IssuePreviewPanelSpotlightUiProps> = ({
	TargetWrapper = Fragment,
	offset,
	placement,
	setIsActive,
}) => {
	const { onClosed } = usePostOfficeUserInteractions();

	const handleClose = useCallback(() => {
		setIsActive?.(false);

		void onClosed({ interactionIdentifier: 'dismiss' });
	}, [onClosed, setIsActive]);

	const handleDismiss = useCallback(() => handleClose(), [handleClose]);

	const handlePrimaryAction = useCallback(() => handleClose(), [handleClose]);

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				handleClose();
			}
		};

		getDocument()?.addEventListener('keydown', handleEscape);

		return () => {
			getDocument()?.removeEventListener('keydown', handleEscape);
		};
	}, [handleClose]);

	return (
		<PopoverProvider>
			<TargetWrapper>
				<PopoverTarget>{null}</PopoverTarget>
			</TargetWrapper>
			<PopoverContent
				dismiss={handleDismiss}
				isVisible={true}
				offset={offset}
				placement={placement}
			>
				<SpotlightCard>
					<SpotlightHeader>
						<SpotlightHeadline>
							<FormattedMessage {...messages.heading} />
						</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl autoFocus={false} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightMedia>
						<Image
							alt="" // Image is purely decorative; no alt text required
							src={issuePreviewPanelSpotlightImage}
						/>
					</SpotlightMedia>
					<SpotlightBody>
						<Text>
							<FormattedMessage {...messages.body} />
						</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightActions>
							<SpotlightPrimaryLink
								href={COMMUNITY_POST_URL}
								onClick={handlePrimaryAction}
								rel="noopener noreferrer"
								target="_blank"
							>
								<FormattedMessage {...messages.more} />
								<VisuallyHidden>
									<FormattedMessage {...messages.newWindow} />
								</VisuallyHidden>
							</SpotlightPrimaryLink>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>
	);
};

const IssuePreviewPanelSpotlightInline: FC<IssuePreviewPanelSpotlightInlineRenderProps> = ({
	setIsActive,
	state,
}) => {
	const hasActivatedRef = useRef(false);

	useEffect(() => {
		if (state === 'inactive' && !hasActivatedRef.current) {
			setIsActive(true);
			hasActivatedRef.current = true;
		}
	}, [setIsActive, state]);

	if (state !== 'active') {
		return null;
	}

	return (
		<IssuePreviewPanelSpotlightUi
			// We need to render the target within the table so the spotlight is
			// correctly positioned, but we can't render the spotlight within
			// the table or it will be trapped in the merged cell's stacking
			// context
			TargetWrapper={RenderToOpenIssueLink}
			offset={[-6, 18]}
			placement="bottom-start"
			setIsActive={setIsActive}
		/>
	);
};

const IssuePreviewPanelSpotlightSidebar: FC<IssuePreviewPanelSpotlightSidebarRenderProps> = () => {
	const renderInline = useMountToHtmlElement({
		selectors: '[data-testid="platform-issue-preview-panel.preview-panel-header"]',
		as: 'inline',
		position: 'before',
	});
	const [isActive, setIsActive] = useState(true);

	if (!isActive) {
		return null;
	}

	return renderInline(
		<IssuePreviewPanelSpotlightUi
			offset={[96, 4]}
			placement="left-end"
			setIsActive={setIsActive}
		/>,
	);
};

export const IssuePreviewPanelSpotlight: FC<
	IssuePreviewPanelSpotlightInlineRenderProps | IssuePreviewPanelSpotlightSidebarRenderProps
> = memo((props) => {
	if (props.placementType === 'issue-preview-panel-spotlight-inline') {
		return <IssuePreviewPanelSpotlightInline {...props} />;
	}

	return <IssuePreviewPanelSpotlightSidebar {...props} />;
});
